import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import { cn } from '@crowdex/ui/lib/utils';
import { FileBox, LayoutGrid, Search } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { ItemIcon } from '@/components/item-icon';
import { useMediaQuery } from '@/hooks/use-media-query';
import { shortcutLabel } from '@/lib/platform';
import { matchingToolEntries, type PanelToolEntry } from '@/lib/side-panel/panel-tools';
import type { SidePanelView } from '@/lib/side-panel/side-panel-items';

const SUGGESTED_ARTIFACT_LIMIT = 3;

/** An address typed in the launcher, as a browser opens it (`localhost:5173` → `http://…`). */
export function launcherUrl(typed: string): string | null {
  const value = typed.trim();
  if (/^https?:\/\//i.test(value)) return value;
  if (/^[a-z][a-z0-9+.-]*:(?!\d)/i.test(value)) return null;
  const local = /^(localhost|127\.0\.0\.1|\[::1\])(:\d+)?(\/|$)/i.test(value);
  return `${local ? 'http' : 'https'}://${value}`;
}

function openInBrowserWindow(typed: string): void {
  const url = launcherUrl(typed);
  if (url !== null) window.open(url, '_blank', 'noopener');
}

function NewTabSection({ children, title }: { children: ReactNode; title: string }) {
  return (
    <section aria-label={title} className="flex flex-col gap-2">
      <h2 className="type-meta text-muted-foreground">{title}</h2>
      {children}
    </section>
  );
}

/** A tool or an app as a tile: its glyph and its name; a dot when it is open already. */
export function ToolTile({
  entry,
  onLaunch,
  compact = false,
}: {
  entry: PanelToolEntry;
  onLaunch: () => void;
  /** Smaller, as the phone's sheet shows them. */
  compact?: boolean;
}) {
  const { description } = entry.item;
  const name = entry.group === 'tool' ? entry.item.label : entry.label;
  return (
    <button
      aria-label={entry.open ? `${name} (open)` : name}
      className={cn(
        'type-meta relative flex min-w-0 flex-col items-center justify-center gap-2 rounded-2xl px-2 text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        // On the phone's sheet (a card itself) the tiles stand out as controls.
        compact
          ? 'h-20 bg-control active:bg-control/70'
          : 'h-[4.75rem] border bg-card hover:bg-control',
      )}
      onClick={onLaunch}
      title={
        [description, entry.shortcut && shortcutLabel(entry.shortcut)]
          .filter(Boolean)
          .join(' · ') || undefined
      }
      type="button"
    >
      {entry.open && (
        <span aria-hidden className="absolute top-2.5 right-2.5 size-1.5 rounded-full bg-running" />
      )}
      <span
        className={cn(
          'flex items-center justify-center text-foreground',
          entry.group === 'app' && 'size-7 rounded-lg',
          entry.group === 'app' && (compact ? 'bg-background/60' : 'bg-control'),
        )}
      >
        <ItemIcon className="size-[18px]" icon={entry.item.icon} src={entry.item.iconSrc} />
      </span>
      <span
        className={cn(
          'w-full text-center font-medium',
          // The phone's tiles are narrow: a long name wraps rather than being cut.
          compact ? 'line-clamp-2 leading-tight' : 'truncate',
        )}
      >
        {name}
      </span>
    </button>
  );
}

/**
 * The last tile of the apps: more apps for the conversation, from the plugins' catalog (the apps
 * the user has are the tiles before it; All apps opens them full screen, not here).
 */
export function AllAppsTile({
  onOpen,
  compact = false,
}: {
  onOpen: () => void;
  compact?: boolean;
}) {
  return (
    <button
      className={cn(
        'type-meta flex min-w-0 flex-col items-center justify-center gap-2 rounded-2xl border border-hairline-strong border-dashed px-2 text-muted-foreground transition-colors hover:bg-card hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        compact ? 'h-20' : 'h-[4.75rem]',
      )}
      onClick={onOpen}
      type="button"
    >
      <LayoutGrid className="size-[18px]" />
      <span className="truncate">More apps</span>
    </button>
  );
}

/**
 * A fresh panel tab, the launcher of the panel (D9; the phone's sheet 7 is the same): one field to
 * search tools and apps or enter an address, then the tools as tiles in their fixed order, the
 * apps of the conversation with "More apps", and suggestions from what the conversation made.
 * Picking one replaces the new tab (an open tool's tab is selected).
 */
export function NewTabView({
  artifacts,
  tools,
  onAllApps,
  onLaunch,
  onOpenUrl,
  onOpenView,
}: {
  artifacts: readonly ArtifactListingEntry[];
  tools: readonly PanelToolEntry[];
  onAllApps?: () => void;
  onLaunch: (entry: PanelToolEntry) => void;
  onOpenUrl?: (url: string) => void;
  onOpenView: (view: SidePanelView) => void;
}) {
  const [query, setQuery] = useState('');
  const touchScreen = useMediaQuery('(pointer: coarse)');
  const searchRef = useRef<HTMLInputElement>(null);
  // A touch screen would open its keyboard over the launcher.
  useEffect(() => {
    if (!touchScreen) searchRef.current?.focus();
  }, [touchScreen]);
  const searching = query.trim().length > 0;
  const shown = matchingToolEntries(tools, query);
  const coreTools = shown.filter((entry) => entry.group !== 'app');
  const apps = shown.filter((entry) => entry.group === 'app');
  const suggested = searching ? [] : artifacts.slice(0, SUGGESTED_ARTIFACT_LIMIT);
  // Without the panel's browser, an address opens in a window of the user's own browser.
  const openUrl = onOpenUrl ?? openInBrowserWindow;
  const placeholder = 'Search tools, apps, or enter a URL';
  return (
    <div className="h-full min-h-0 overflow-y-auto px-4 pt-1 pb-6">
      <div className="desktop-panel-view-enter @container mx-auto flex max-w-2xl flex-col gap-5">
        <form
          className="flex h-10 items-center gap-2.5 rounded-control border bg-card px-4"
          onSubmit={(event) => {
            event.preventDefault();
            const first = coreTools[0] ?? apps[0];
            if (/[./:]/.test(query.trim())) openUrl(query.trim());
            else if (searching && first !== undefined) onLaunch(first);
          }}
        >
          <Search className="size-4 shrink-0 text-muted-foreground" />
          <input
            aria-label={placeholder}
            ref={searchRef}
            className="type-ui min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
            onChange={(event) => setQuery(event.target.value)}
            placeholder={placeholder}
            value={query}
          />
        </form>
        <NewTabSection title="Tools">
          {coreTools.length === 0 ? (
            <p className="type-meta text-muted-foreground">No matching tool.</p>
          ) : (
            <div className="grid grid-cols-3 gap-2 @[26rem]:grid-cols-4">
              {coreTools.map((entry) => (
                <ToolTile entry={entry} key={entry.key} onLaunch={() => onLaunch(entry)} />
              ))}
            </div>
          )}
        </NewTabSection>
        {(apps.length > 0 || (!searching && onAllApps !== undefined)) && (
          <NewTabSection title="Apps on this conversation">
            <div className="grid grid-cols-3 gap-2 @[26rem]:grid-cols-4">
              {apps.map((entry) => (
                <ToolTile entry={entry} key={entry.key} onLaunch={() => onLaunch(entry)} />
              ))}
              {!searching && onAllApps !== undefined && <AllAppsTile onOpen={onAllApps} />}
            </div>
          </NewTabSection>
        )}
        {suggested.length > 0 && (
          <NewTabSection title="Suggested">
            <div className="flex flex-col gap-2">
              {suggested.map((artifact) => (
                <button
                  className="flex min-w-0 items-center gap-3 rounded-2xl bg-card px-3 py-2.5 text-left transition-colors hover:bg-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  key={artifact.id}
                  onClick={() => onOpenView(`artifact:${artifact.id}`)}
                  title={artifact.title}
                  type="button"
                >
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-control">
                    <FileBox className="size-4 text-foreground" />
                  </span>
                  <span className="flex min-w-0 flex-col">
                    <span className="type-ui truncate font-medium">{artifact.title}</span>
                    <span className="type-caption truncate text-muted-foreground">
                      {artifact.typeTitle} from this conversation
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </NewTabSection>
        )}
      </div>
    </div>
  );
}
